import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { AlertTriangle, Trash2, ShieldAlert } from 'lucide-react';
import { TenantDetail } from '../../../types/domain';

interface TenantDangerZoneProps {
  tenant: TenantDetail;
  isSelf: boolean;
  isDeletingUser: boolean;
  onDeleteTenant: () => void;
}

export const TenantDangerZone: React.FC<TenantDangerZoneProps> = ({
  tenant,
  isSelf,
  isDeletingUser,
  onDeleteTenant,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  return (
    <>
      <Card className="border-rose-500/30">
        <CardHeader className="bg-rose-500/5">
          <CardTitle className="text-rose-600 dark:text-rose-400">
            <AlertTriangle size={16} />
            <span>Danger Zone</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded bg-cf-bg border border-cf-border text-xs">
            <div>
              <div className="font-semibold text-cf-textPrimary">Delete Tenant Account</div>
              <div className="text-cf-textSecondary mt-0.5">
                Permanently purge this user, their organization memberships, and active tokens.
              </div>
            </div>
            <Button
              variant="danger"
              size="sm"
              disabled={isSelf}
              leftIcon={<Trash2 size={13} />}
              onClick={() => {
                setConfirmInput('');
                setDeleteModalOpen(true);
              }}
            >
              {isSelf ? 'Cannot Delete Self' : 'Delete Account'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Permanent Deletion"
        maxWidth="md"
      >
        <div className="space-y-4 text-xs">
          <div className="p-3 rounded bg-rose-500/10 border border-rose-500/30 flex items-start gap-2.5 text-rose-600 dark:text-rose-400">
            <ShieldAlert size={18} className="shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              This action is <strong>irreversible</strong>. All project memberships, API tokens, and user credentials will be permanently erased.
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-cf-textSecondary font-medium">
              Type <span className="font-mono font-bold text-cf-textPrimary">{tenant.email}</span> to confirm:
            </label>
            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={tenant.email}
              mono
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-cf-border">
            <Button variant="secondary" size="sm" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={confirmInput !== tenant.email}
              isLoading={isDeletingUser}
              onClick={() => {
                setDeleteModalOpen(false);
                onDeleteTenant();
              }}
            >
              Permanently Delete Tenant
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
